import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState";
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectJobDetailsSection, selectFieldDescriptions } from "../../../redux/selectors";
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";

function GeneralElectrical({ zone }) {
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;

  const apiUrls = useSelector(selectApiUrls);
  const sectionName = "generalStation";
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown);

  const error = null;

  const storedJobDetails = useSelector((state) =>
    selectJobDetailsSection(state, "generalStation")
  );

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );
  const jobDetails = useMemo(
    () => storedJobDetails.find((detail) => detail.Zone === activetab) || {},
    [storedJobDetails, activetab]
  );
  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        siteVoltage: jobDetails.SiteVoltage || "",
        powerConfiguration: jobDetails.PowerConfiguration || "",
        sccrRating: jobDetails.SCCRRating || "",
        fccType: jobDetails.FCCType || "",
      });
    }
  }, [jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const siteVoltageOptions =
    options
      ?.filter((option) => option.DropdownType === "Site Voltage")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const powerConfigurationOptions =
    options
      ?.filter((option) => option.DropdownType === "Power Configuration")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const fccTypeOptions =
    options
      ?.filter((option) => option.DropdownType === "FCC Type")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const sccrRatingOptions =
    options
      ?.filter((option) => option.DropdownType === "SCCR Rating")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  // const { tbdFields } = useTbdCheck(formData, zone);
  // const zoneTbdFields = zone ? tbdFields?.[zone] : tbdFields;

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    ); // Show no record found alert if no job details
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${saveStatus.includes("Failed") ? "error" : ""}`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <Form onSubmit={handleSubmit} className="mt-4">
            <Row className="mb-3 row-cols-1 row-cols-sm-4 rows-cols-lg-6">
              <Col>
                <Form.Group as={Row} controlId="siteVoltage">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SiteVoltage")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Site Voltage</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="siteVoltage"
                      value={formData.siteVoltage}
                      onFocus={handleFocus} // Capture the current value on focus
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Site Voltage",
                          siteVoltageOptions, // You can pass any options here if needed
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "SiteVoltage",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.siteVoltage} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Site Voltage</option>
                      {siteVoltageOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.siteVoltage,
                        siteVoltageOptions
                      ) &&
                        formData.siteVoltage && (
                          <option
                            value={formData.siteVoltage}
                            style={{ color: "red" }}
                          >
                            *{formData.siteVoltage}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="powerConfiguration">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("PowerConfiguration")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Power Configuration</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="powerConfiguration"
                      value={formData.powerConfiguration}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Power Configuration",
                          powerConfigurationOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "PowerConfiguration",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.powerConfiguration} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Power Configuration</option>
                      {powerConfigurationOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.powerConfiguration,
                        powerConfigurationOptions
                      ) &&
                        formData.powerConfiguration && (
                          <option
                            value={formData.powerConfiguration}
                            style={{ color: "red" }}
                          >
                            *{formData.powerConfiguration}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="sccrRating">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SCCRRating")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>SCCR Rating</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="sccrRating"
                      value={formData.sccrRating}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "SCCR Rating",
                          sccrRatingOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "SCCRRating",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.sccrRating} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select SCCR Rating</option>
                      {sccrRatingOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(
                        formData.sccrRating,
                        sccrRatingOptions
                      ) &&
                        formData.sccrRating && (
                          <option
                            value={formData.sccrRating}
                            style={{ color: "red" }}
                          >
                            *{formData.sccrRating}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="fccType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("FCCType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Fcc Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col md={9}>
                    <Form.Select
                      name="fccType"
                      value={formData.fccType}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Fcc Type", fccTypeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "FCCType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.fccType} // Apply dynamic class
                      onChange={handleChange}
                    >
                      <option value="">Select Fcc Type</option>
                      {fccTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.fccType, fccTypeOptions) &&
                        formData.fccType && (
                          <option
                            value={formData.fccType}
                            style={{ color: "red" }}
                          >
                            *{formData.fccType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </div>
  );
}

export default GeneralElectrical;
